import { ModuleWrapper } from '@/components'
import type {
  VehicleDocumentsQuery,
  VehicleDocumentsQueryVariables,
} from '@/modules/vehicles'
import { VEHICLE_DOCUMENTS_QUERY } from '@/modules/vehicles'
import type { VehicleListFilterForm } from '@/modules/vehicles/list-filters'
import {
  emptyVehicleListFilter,
  getVehicleExpirationFilterVariables,
  VehicleListFilters,
} from '@/modules/vehicles/list-filters'
import { VehicleListExportButton } from '@/modules/vehicles/list-export'
import { useDebounce, useNodes, useOnErrorAuto } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { useVehicleDocumentHandlers } from './use-vehicle-document-handlers'
import { VehicleDocumentsTable } from './vehicle-documents-table'

export const VehicleDocumentsView: React.FC = () => {
  const translations = useTranslate({
    title: 'vehicles.vehicle-documents',
    searchPlaceholder: 'vehicles.documents.search-placeholder',
  })

  const [filter, setFilter] = useState<VehicleListFilterForm>(
    emptyVehicleListFilter
  )

  // Only the free-text search is debounced; the other filters change one click
  // at a time and should apply immediately.
  const debouncedQ = useDebounce(filter.q)

  const filterVariables = useMemo(
    () => getVehicleExpirationFilterVariables({ ...filter, q: debouncedQ }),
    [filter, debouncedQ]
  )

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<
    VehicleDocumentsQuery,
    VehicleDocumentsQueryVariables
  >(VEHICLE_DOCUMENTS_QUERY, {
    variables: { ...filterVariables, orderBy: 'expirationDate' },
    onError: onErrorAuto(),
  })

  const documents = useNodes(data?.allVehicleDocuments)

  const { editDocument, removeDocument } = useVehicleDocumentHandlers()

  return (
    <ModuleWrapper
      title={translations.title}
      headerRight={
        <VehicleListExportButton kind="documents" filters={filterVariables} />
      }
    >
      <VehicleListFilters
        filter={filter}
        searchPlaceholder={translations.searchPlaceholder}
        onChange={patch => setFilter(current => ({ ...current, ...patch }))}
        onClear={() => setFilter(emptyVehicleListFilter)}
      />

      <VehicleDocumentsTable
        documents={documents}
        loading={loading}
        onEdit={editDocument}
        onDelete={removeDocument}
      />
    </ModuleWrapper>
  )
}
